import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import DeferredNotificationHosts from "@/components/notifications/DeferredNotificationHosts";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
const HomePage = lazy(() => import("./pages/HomePage"));
const EnterprisePage = lazy(() => import("./pages/EnterprisePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
const IdentifyPage = lazy(() => import("./pages/IdentifyPage"));
const QuickIdPage = lazy(() => import("./pages/QuickIdPage"));
const PropertyGuidePage = lazy(() => import("./pages/PropertyGuidePage"));
const ComparisonGuidePage = lazy(() => import("./pages/ComparisonGuidePage"));
const IdentificationGuidePage = lazy(() => import("./pages/IdentificationGuidePage"));
const IdentificationChartPage = lazy(() => import("./pages/IdentificationChartPage"));
const CommonMineralChartPage = lazy(() => import("./pages/CommonMineralChartPage"));
const MohsHardnessPage = lazy(() => import("./pages/MohsHardnessPage"));
const MineralWorksheetPage = lazy(() => import("./pages/MineralWorksheetPage"));
const RockCyclePage = lazy(() => import("./pages/RockCyclePage"));
const SpecificGravityPage = lazy(() => import("./pages/SpecificGravityPage"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const CalculatorHubPage = lazy(() => import("./pages/CalculatorHubPage"));
const StreakTestPage = lazy(() => import("./pages/StreakTestPage"));
const CleavageFracturePage = lazy(() => import("./pages/CleavageFracturePage"));
const MineralLustrePage = lazy(() => import("./pages/MineralLustrePage"));
const MineralColourPage = lazy(() => import("./pages/MineralColourPage"));
const MineralMagnetismPage = lazy(() => import("./pages/MineralMagnetismPage"));
const MineralTransparencyPage = lazy(() => import("./pages/MineralTransparencyPage"));
const MineralHabitPage = lazy(() => import("./pages/MineralHabitPage"));
const CrystalIdentificationGuidePage = lazy(() => import("./pages/CrystalIdentificationGuidePage"));
const GeodeIdentificationGuidePage = lazy(() => import("./pages/GeodeIdentificationGuidePage"));
const MeteoriteIdentificationGuidePage = lazy(() => import("./pages/MeteoriteIdentificationGuidePage"));
const MineralFluorescencePage = lazy(() => import("./pages/MineralFluorescencePage"));
const RockhoundingBeginnerGuidePage = lazy(() => import("./pages/RockhoundingBeginnerGuidePage"));
const OntarioRockIdentificationGuidePage = lazy(() => import("./pages/OntarioRockIdentificationGuidePage"));
const RockTumblingBeginnerGuidePage = lazy(() => import("./pages/RockTumblingBeginnerGuidePage"));
const SpecimenLabelMakerPage = lazy(() => import("./pages/SpecimenLabelMakerPage"));
const CollectionInventoryPage = lazy(() => import("./pages/CollectionInventoryPage"));
const GeologicTimeScalePage = lazy(() => import("./pages/GeologicTimeScalePage"));
const StrikeDipCalculatorPage = lazy(() => import("./pages/StrikeDipCalculatorPage"));
const StereonetPlotterPage = lazy(() => import("./pages/StereonetPlotterPage"));
const SedimentGrainSizeCalculatorPage = lazy(() => import("./pages/SedimentGrainSizeCalculatorPage"));
const SoilTextureCalculatorPage = lazy(() => import("./pages/SoilTextureCalculatorPage"));
const AtterbergLimitsCalculatorPage = lazy(() => import("./pages/AtterbergLimitsCalculatorPage"));
const SoilCompactionCalculatorPage = lazy(() => import("./pages/SoilCompactionCalculatorPage"));
const GrainSizeDistributionCalculatorPage = lazy(() => import("./pages/GrainSizeDistributionCalculatorPage"));
const UscsSoilClassificationCalculatorPage = lazy(() => import("./pages/UscsSoilClassificationCalculatorPage"));
const RqdCalculatorPage = lazy(() => import("./pages/RqdCalculatorPage"));
const RockMassClassificationGuidePage = lazy(() => import("./pages/RockMassClassificationGuidePage"));
const PointLoadStrengthCalculatorPage = lazy(() => import("./pages/PointLoadStrengthCalculatorPage"));
const UniaxialCompressiveStrengthCalculatorPage = lazy(() => import("./pages/UniaxialCompressiveStrengthCalculatorPage"));
const RockMassRatingCalculatorPage = lazy(() => import("./pages/RockMassRatingCalculatorPage"));
const QSystemCalculatorPage = lazy(() => import("./pages/QSystemCalculatorPage"));
const RockWeightCalculatorPage = lazy(() => import("./pages/RockWeightCalculatorPage"));
const PorosityCalculatorPage = lazy(() => import("./pages/PorosityCalculatorPage"));
const DarcyFlowCalculatorPage = lazy(() => import("./pages/DarcyFlowCalculatorPage"));
const AquiferTransmissivityCalculatorPage = lazy(() => import("./pages/AquiferTransmissivityCalculatorPage"));
const RadiometricDatingCalculatorPage = lazy(() => import("./pages/RadiometricDatingCalculatorPage"));
const GeothermalGradientCalculatorPage = lazy(() => import("./pages/GeothermalGradientCalculatorPage"));
const HydraulicConductivityLabCalculatorPage = lazy(() => import("./pages/HydraulicConductivityLabCalculatorPage"));
const LugeonPackerTestCalculatorPage = lazy(() => import("./pages/LugeonPackerTestCalculatorPage"));
const TheisDrawdownCalculatorPage = lazy(() => import("./pages/TheisDrawdownCalculatorPage"));
const SlopeGradientCalculatorPage = lazy(() => import("./pages/SlopeGradientCalculatorPage"));
const CoordinateConverterPage = lazy(() => import("./pages/CoordinateConverterPage"));
const MapScaleCalculatorPage = lazy(() => import("./pages/MapScaleCalculatorPage"));
const CoordinateDistanceCalculatorPage = lazy(() => import("./pages/CoordinateDistanceCalculatorPage"));
const TrueThicknessCalculatorPage = lazy(() => import("./pages/TrueThicknessCalculatorPage"));
const UtmCoordinateConverterPage = lazy(() => import("./pages/UtmCoordinateConverterPage"));
const OreGradeConverterPage = lazy(() => import("./pages/OreGradeConverterPage"));
const ContainedMetalCalculatorPage = lazy(() => import("./pages/ContainedMetalCalculatorPage"));
const MiningCutoffGradeCalculatorPage = lazy(() => import("./pages/MiningCutoffGradeCalculatorPage"));
const MineDilutionCalculatorPage = lazy(() => import("./pages/MineDilutionCalculatorPage"));
const StrippingRatioCalculatorPage = lazy(() => import("./pages/StrippingRatioCalculatorPage"));
const WeightedIntervalGradeCalculatorPage = lazy(() => import("./pages/WeightedIntervalGradeCalculatorPage"));
const DrillholeDesurveyCalculatorPage = lazy(() => import("./pages/DrillholeDesurveyCalculatorPage"));
const EarthquakeMagnitudeCalculatorPage = lazy(() => import("./pages/EarthquakeMagnitudeCalculatorPage"));
const SpecimenCleaningGuidePage = lazy(() => import("./pages/SpecimenCleaningGuidePage"));
const RockValueGuidePage = lazy(() => import("./pages/RockValueGuidePage"));
const IgneousRockGuidePage = lazy(() => import("./pages/IgneousRockGuidePage"));
const SedimentaryRockGuidePage = lazy(() => import("./pages/SedimentaryRockGuidePage"));
const MetamorphicRockGuidePage = lazy(() => import("./pages/MetamorphicRockGuidePage"));
const FossilIdentificationGuidePage = lazy(() => import("./pages/FossilIdentificationGuidePage"));
const FossilIdentificationQuizPage = lazy(() => import("./pages/FossilIdentificationQuizPage"));
const RockIdentificationQuizPage = lazy(() => import("./pages/RockIdentificationQuizPage"));
const MineralIdentificationQuizPage = lazy(() => import("./pages/MineralIdentificationQuizPage"));
const MyFindsPage = lazy(() => import("./pages/MyFindsPage"));
const FindDetailPage = lazy(() => import("./pages/FindDetailPage"));
const PublicFindsPage = lazy(() => import("./pages/PublicFindsPage"));
const PublicFindArchivePage = lazy(() => import("./pages/PublicFindArchivePage"));
const ProToolsPage = lazy(() => import("./pages/ProToolsPage"));
const TrainingPage = lazy(() => import("./pages/TrainingPage"));
const ReferencePage = lazy(() => import("./pages/ReferencePage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const VerifyPage = lazy(() => import("./pages/VerifyPage"));
const CertificatesPage = lazy(() => import("./pages/CertificatesPage"));
const OrganizationPage = lazy(() => import("./pages/OrganizationPage"));
const CoreLoggingPage = lazy(() => import("./pages/pro-tools/CoreLoggingPage"));
const DrillProgramPage = lazy(() => import("./pages/pro-tools/DrillProgramPage"));
const SamplingPage = lazy(() => import("./pages/pro-tools/SamplingPage"));
const MappingNotesPage = lazy(() => import("./pages/pro-tools/MappingNotesPage"));
const ProductionPage = lazy(() => import("./pages/pro-tools/ProductionPage"));
const ExportsPage = lazy(() => import("./pages/pro-tools/ExportsPage"));
const TrackDetailPage = lazy(() => import("./pages/training/TrackDetailPage"));
const LessonPage = lazy(() => import("./pages/training/LessonPage"));
const ReferenceListPage = lazy(() => import("./pages/reference/ReferenceListPage"));
const ReferenceDetailPage = lazy(() => import("./pages/reference/ReferenceDetailPage"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then((module) => ({ default: module.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminModeration = lazy(() => import("./pages/admin/AdminModeration"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminCosts = lazy(() => import("./pages/admin/AdminCosts"));
const AdminFinds = lazy(() => import("./pages/admin/AdminFinds"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const CareerCategoryPage = lazy(() => import("./pages/CareerCategoryPage"));
const CareerComparePage = lazy(() => import("./pages/CareerComparePage"));
const CareerSalaryPage = lazy(() => import("./pages/CareerSalaryPage"));
const MiningJobGuidePage = lazy(() => import("./pages/MiningJobGuidePage"));
const CareerDetailPage = lazy(() => import("./pages/CareerDetailPage"));
const CampLifePage = lazy(() => import("./pages/CampLifePage"));
const WorkforceOutlookPage = lazy(() => import("./pages/WorkforceOutlookPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const CareerQuizPage = lazy(() => import("./pages/CareerQuizPage"));
const ClassroomLandingPage = lazy(() => import("./pages/classroom/ClassroomLandingPage"));
const CreateClassroomPage = lazy(() => import("./pages/classroom/CreateClassroomPage"));
const ClassroomDashboardPage = lazy(() => import("./pages/classroom/ClassroomDashboardPage"));
const JoinClassroomPage = lazy(() => import("./pages/classroom/JoinClassroomPage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const ForGeologistsPage = lazy(() => import("./pages/ForGeologistsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const InstallPage = lazy(() => import("./pages/InstallPage"));
const GamificationBootstrap = lazy(() => import("./components/gamification/GamificationBootstrap"));

import { RouteAnalytics } from "./components/analytics/RouteAnalytics";

const FloatingAssistant = lazy(() =>
  import("./components/ai/FloatingAssistant").then((module) => ({ default: module.FloatingAssistant })),
);

const queryClient = new QueryClient();

type IdleCapableWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

function AuthenticatedGamification() {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  return <Suspense fallback={null}><GamificationBootstrap /></Suspense>;
}

function DeferredFloatingAssistant() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const idleWindow = window as IdleCapableWindow;
    let idleHandle: number | undefined;
    let timerHandle: number | undefined;
    let waitingForLoad = false;

    const activate = () => setReady(true);
    const scheduleIdle = () => {
      timerHandle = undefined;
      if (idleWindow.requestIdleCallback) {
        idleHandle = idleWindow.requestIdleCallback(activate, { timeout: 3_000 });
      } else {
        activate();
      }
    };
    const schedule = () => {
      waitingForLoad = false;
      timerHandle = window.setTimeout(scheduleIdle, 3_000);
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      waitingForLoad = true;
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      if (waitingForLoad) window.removeEventListener("load", schedule);
      if (idleHandle !== undefined) idleWindow.cancelIdleCallback?.(idleHandle);
      if (timerHandle !== undefined) window.clearTimeout(timerHandle);
    };
  }, []);

  if (!ready) return null;
  return <Suspense fallback={null}><FloatingAssistant /></Suspense>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <DeferredNotificationHosts />
      <AuthenticatedGamification />
      <BrowserRouter>
          <RouteAnalytics />
          <ScrollToTop />
          <MobileBottomNav />
          <DeferredFloatingAssistant />
          <Suspense fallback={<div className="min-h-[50vh]" aria-label="Loading page" />}>
          <Routes>
            {/* All routes use MainLayout for consistent navigation */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/identify" element={<IdentifyPage />} />
              <Route path="/identify/:guideSlug" element={<PropertyGuidePage />} />
              <Route path="/compare" element={<ComparisonGuidePage />} />
              <Route path="/compare/:comparisonSlug" element={<ComparisonGuidePage />} />
              <Route path="/guides/rock-and-mineral-identification" element={<IdentificationGuidePage />} />
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/tools" element={<CalculatorHubPage />} />
              <Route path="/guides/rock-and-mineral-identification-chart" element={<IdentificationChartPage />} />
              <Route path="/guides/mineral-identification-chart" element={<CommonMineralChartPage />} />
              <Route path="/guides/mohs-hardness-scale" element={<MohsHardnessPage />} />
              <Route path="/guides/mineral-streak-test-chart" element={<StreakTestPage />} />
              <Route path="/guides/mineral-cleavage-fracture-chart" element={<CleavageFracturePage />} />
              <Route path="/guides/mineral-luster-chart" element={<MineralLustrePage />} />
              <Route path="/guides/mineral-color-chart" element={<MineralColourPage />} />
              <Route path="/guides/mineral-magnetism-test-chart" element={<MineralMagnetismPage />} />
              <Route path="/guides/mineral-transparency-chart" element={<MineralTransparencyPage />} />
              <Route path="/guides/mineral-crystal-habit-chart" element={<MineralHabitPage />} />
              <Route path="/guides/crystal-identification" element={<CrystalIdentificationGuidePage />} />
              <Route path="/guides/geode-identification" element={<GeodeIdentificationGuidePage />} />
              <Route path="/guides/meteorite-identification" element={<MeteoriteIdentificationGuidePage />} />
              <Route path="/guides/mineral-fluorescence-chart" element={<MineralFluorescencePage />} />
              <Route path="/guides/rockhounding-for-beginners" element={<RockhoundingBeginnerGuidePage />} />
              <Route path="/guides/ontario-rock-identification" element={<OntarioRockIdentificationGuidePage />} />
              <Route path="/guides/rock-tumbling-for-beginners" element={<RockTumblingBeginnerGuidePage />} />
              <Route path="/tools/rock-mineral-specimen-label-maker" element={<SpecimenLabelMakerPage />} />
              <Route path="/tools/rock-mineral-collection-inventory" element={<CollectionInventoryPage />} />
              <Route path="/guides/geologic-time-scale" element={<GeologicTimeScalePage />} />
              <Route path="/tools/strike-dip-calculator" element={<StrikeDipCalculatorPage />} />
              <Route path="/tools/stereonet-plotter" element={<StereonetPlotterPage />} />
              <Route path="/tools/sediment-grain-size-calculator" element={<SedimentGrainSizeCalculatorPage />} />
              <Route path="/tools/soil-texture-triangle-calculator" element={<SoilTextureCalculatorPage />} />
              <Route path="/tools/atterberg-limits-plasticity-index-calculator" element={<AtterbergLimitsCalculatorPage />} />
              <Route path="/tools/soil-dry-density-relative-compaction-calculator" element={<SoilCompactionCalculatorPage />} />
              <Route path="/tools/sieve-analysis-grain-size-distribution-calculator" element={<GrainSizeDistributionCalculatorPage />} />
              <Route path="/tools/uscs-soil-classification-calculator" element={<UscsSoilClassificationCalculatorPage />} />
              <Route path="/tools/core-recovery-rqd-calculator" element={<RqdCalculatorPage />} />
              <Route path="/guides/rock-mass-classification" element={<RockMassClassificationGuidePage />} />
              <Route path="/tools/point-load-strength-index-calculator" element={<PointLoadStrengthCalculatorPage />} />
              <Route path="/tools/uniaxial-compressive-strength-calculator" element={<UniaxialCompressiveStrengthCalculatorPage />} />
              <Route path="/tools/rock-mass-rating-rmr-calculator" element={<RockMassRatingCalculatorPage />} />
              <Route path="/tools/q-system-rock-mass-calculator" element={<QSystemCalculatorPage />} />
              <Route path="/tools/rock-weight-calculator" element={<RockWeightCalculatorPage />} />
              <Route path="/tools/porosity-void-ratio-calculator" element={<PorosityCalculatorPage />} />
              <Route path="/tools/darcy-law-groundwater-flow-calculator" element={<DarcyFlowCalculatorPage />} />
              <Route path="/tools/aquifer-transmissivity-calculator" element={<AquiferTransmissivityCalculatorPage />} />
              <Route path="/tools/radiometric-dating-calculator" element={<RadiometricDatingCalculatorPage />} />
              <Route path="/tools/geothermal-gradient-calculator" element={<GeothermalGradientCalculatorPage />} />
              <Route path="/tools/hydraulic-conductivity-permeameter-calculator" element={<HydraulicConductivityLabCalculatorPage />} />
              <Route path="/tools/lugeon-packer-test-calculator" element={<LugeonPackerTestCalculatorPage />} />
              <Route path="/tools/theis-well-drawdown-calculator" element={<TheisDrawdownCalculatorPage />} />
              <Route path="/tools/slope-gradient-calculator" element={<SlopeGradientCalculatorPage />} />
              <Route path="/tools/latitude-longitude-coordinate-converter" element={<CoordinateConverterPage />} />
              <Route path="/tools/map-scale-distance-calculator" element={<MapScaleCalculatorPage />} />
              <Route path="/tools/distance-between-coordinates-calculator" element={<CoordinateDistanceCalculatorPage />} />
              <Route path="/tools/true-thickness-calculator" element={<TrueThicknessCalculatorPage />} />
              <Route path="/tools/utm-coordinate-converter" element={<UtmCoordinateConverterPage />} />
              <Route path="/tools/ore-grade-unit-converter" element={<OreGradeConverterPage />} />
              <Route path="/tools/contained-metal-calculator" element={<ContainedMetalCalculatorPage />} />
              <Route path="/tools/mining-cutoff-grade-calculator" element={<MiningCutoffGradeCalculatorPage />} />
              <Route path="/tools/mine-dilution-ore-loss-calculator" element={<MineDilutionCalculatorPage />} />
              <Route path="/tools/stripping-ratio-calculator" element={<StrippingRatioCalculatorPage />} />
              <Route path="/tools/weighted-average-drill-intercept-grade-calculator" element={<WeightedIntervalGradeCalculatorPage />} />
              <Route path="/tools/drill-hole-desurvey-calculator" element={<DrillholeDesurveyCalculatorPage />} />
              <Route path="/tools/earthquake-magnitude-energy-calculator" element={<EarthquakeMagnitudeCalculatorPage />} />
              <Route path="/guides/how-to-clean-rocks-and-minerals" element={<SpecimenCleaningGuidePage />} />
              <Route path="/guides/is-my-rock-valuable" element={<RockValueGuidePage />} />
              <Route path="/guides/igneous-rock-identification-chart" element={<IgneousRockGuidePage />} />
              <Route path="/guides/sedimentary-rock-identification-chart" element={<SedimentaryRockGuidePage />} />
              <Route path="/guides/metamorphic-rock-identification-chart" element={<MetamorphicRockGuidePage />} />
              <Route path="/guides/fossil-identification" element={<FossilIdentificationGuidePage />} />
              <Route path="/quiz/rock-identification" element={<RockIdentificationQuizPage />} />
              <Route path="/quiz/mineral-identification" element={<MineralIdentificationQuizPage />} />
              <Route path="/quiz/fossil-identification" element={<FossilIdentificationQuizPage />} />
              <Route path="/guides/mineral-identification-worksheet" element={<MineralWorksheetPage />} />
              <Route path="/guides/rock-cycle" element={<RockCyclePage />} />
              <Route path="/tools/specific-gravity-calculator" element={<SpecificGravityPage />} />
              <Route path="/quick-id" element={<QuickIdPage />} />
              <Route path="/my-finds" element={<MyFindsPage />} />
              <Route path="/my-finds/:findId" element={<FindDetailPage />} />
              <Route path="/finds" element={<PublicFindsPage />} />
              <Route path="/finds/page/:pageNumber" element={<PublicFindArchivePage />} />
              <Route path="/finds/:findId" element={<FindDetailPage />} />
              <Route path="/pro-tools" element={<ProToolsPage />} />
              <Route path="/pro-tools/core-logging" element={<CoreLoggingPage />} />
              <Route path="/pro-tools/drill-program" element={<DrillProgramPage />} />
              <Route path="/pro-tools/sampling" element={<SamplingPage />} />
              <Route path="/pro-tools/mapping" element={<MappingNotesPage />} />
              <Route path="/pro-tools/production" element={<ProductionPage />} />
              <Route path="/pro-tools/exports" element={<ExportsPage />} />
              <Route path="/training" element={<TrainingPage />} />
              <Route path="/training/tracks/:trackId" element={<TrackDetailPage />} />
              <Route path="/training/lessons/:lessonId" element={<LessonPage />} />
              <Route path="/training/certificates" element={<CertificatesPage />} />
              <Route path="/reference" element={<ReferencePage />} />
              <Route path="/reference/:category" element={<ReferenceListPage />} />
              <Route path="/reference/:category/:name" element={<ReferenceDetailPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/organization" element={<OrganizationPage />} />
              <Route path="/stats" element={<StatsPage />} />
              <Route path="/verify/:certificateNumber" element={<VerifyPage />} />
              
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/methodology" element={<MethodologyPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/camp-life" element={<CampLifePage />} />
              <Route path="/careers/outlook" element={<WorkforceOutlookPage />} />
              <Route path="/careers/quiz" element={<CareerQuizPage />} />
              <Route path="/careers/compare" element={<CareerComparePage />} />
              <Route path="/careers/salaries" element={<CareerSalaryPage />} />
              <Route path="/careers/how-to-get-a-job-in-mining" element={<MiningJobGuidePage />} />
              <Route path="/careers/geoscience" element={<CareerCategoryPage categorySlug="geoscience" />} />
              <Route path="/careers/engineering" element={<CareerCategoryPage categorySlug="engineering" />} />
              <Route path="/careers/environment" element={<CareerCategoryPage categorySlug="environment" />} />
              <Route path="/careers/business" element={<CareerCategoryPage categorySlug="business" />} />
              <Route path="/careers/technology" element={<CareerCategoryPage categorySlug="technology" />} />
              <Route path="/careers/trades" element={<CareerCategoryPage categorySlug="trades" />} />
              <Route path="/careers/:slug" element={<CareerDetailPage />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/for-geologists" element={<ForGeologistsPage />} />
              <Route path="/classroom" element={<ClassroomLandingPage />} />
              <Route path="/classroom/create" element={<CreateClassroomPage />} />
              <Route path="/classroom/:id" element={<ClassroomDashboardPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/profile/:userId" element={<ProfilePage />} />
              <Route path="/install" element={<InstallPage />} />
            </Route>
            
            {/* Standalone pages (no MainLayout nav) */}
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/join" element={<JoinClassroomPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Admin routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="finds" element={<AdminFinds />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="moderation" element={<AdminModeration />} />
              <Route path="feedback" element={<AdminFeedback />} />
              <Route path="costs" element={<AdminCosts />} />
            </Route>
            
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
      </BrowserRouter>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
